import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, Clock, MapPin, User, Phone, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { format } from 'date-fns';

const ConfirmationPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [bookingDetails, setBookingDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hourlyPricingRules, setHourlyPricingRules] = useState([]);

  useEffect(() => {
    loadBookingDetails();
  }, [bookingId]);

  const loadBookingDetails = async () => {
    try {
      const response = await api.get(`/bookings/${bookingId}`);
      setBookingDetails(response.data);
    } catch (error) {
      toast.error('Gagal memuat data booking');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const venueId = bookingDetails?.booking?.venue_id;
    if (venueId) loadPricingSettings(venueId);
  }, [bookingDetails]);

  const loadPricingSettings = async (venueId) => {
    try {
      const response = await api.get('/settings/public');
      const rawMap = response.data?.venue_hourly_pricing_rules;
      const raw = response.data?.hourly_pricing_rules;

      let rulesForVenue = null;
      if (rawMap) {
        try {
          const parsedMap = JSON.parse(rawMap);
          if (parsedMap && typeof parsedMap === 'object') {
            const list = parsedMap?.[venueId];
            if (Array.isArray(list)) rulesForVenue = list;
          }
        } catch {}
      }

      if (rulesForVenue) {
        setHourlyPricingRules(rulesForVenue);
      } else if (raw) {
        const parsed = JSON.parse(raw);
        setHourlyPricingRules(Array.isArray(parsed) ? parsed : []);
      } else {
        setHourlyPricingRules([]);
      }
    } catch {
      setHourlyPricingRules([]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const booking = bookingDetails?.booking;
  const venue = bookingDetails?.venue;
  const payment = bookingDetails?.payment;

  const getDayType = (dateStr) => {
    const dt = dateStr ? new Date(`${dateStr}T00:00:00`) : null;
    if (!dt || Number.isNaN(dt.getTime())) return 'weekday';
    const day = dt.getDay();
    return day === 0 || day === 6 ? 'weekend' : 'weekday';
  };

  const getHourlyRate = (hour, dayType) => {
    const rules = Array.isArray(hourlyPricingRules) ? hourlyPricingRules : [];
    const day = String(dayType || 'all').toLowerCase();
    for (const rule of rules) {
      const startHour = Number(rule?.start_hour ?? rule?.startHour);
      const endHour = Number(rule?.end_hour ?? rule?.endHour);
      const pricePerHour = Number(rule?.price_per_hour ?? rule?.pricePerHour);
      const ruleDayType = String(rule?.day_type ?? rule?.dayType ?? 'all').toLowerCase();
      if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) continue;
      if (!Number.isFinite(pricePerHour) || pricePerHour <= 0) continue;
      if (ruleDayType === day && hour >= startHour && hour < endHour) return pricePerHour;
    }
    for (const rule of rules) {
      const startHour = Number(rule?.start_hour ?? rule?.startHour);
      const endHour = Number(rule?.end_hour ?? rule?.endHour);
      const pricePerHour = Number(rule?.price_per_hour ?? rule?.pricePerHour);
      const ruleDayType = String(rule?.day_type ?? rule?.dayType ?? 'all').toLowerCase();
      if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) continue;
      if (!Number.isFinite(pricePerHour) || pricePerHour <= 0) continue;
      if (ruleDayType === 'all' && hour >= startHour && hour < endHour) return pricePerHour;
    }
    return null;
  };

  const calculateTotalAmount = (timeSlots, bookingDate) => {
    const slotsArr = Array.isArray(timeSlots) ? timeSlots : [];
    if (slotsArr.length === 0) return 0;
    const dayType = getDayType(bookingDate);
    let total = 0;
    for (const slot of slotsArr) {
      const match = typeof slot === 'string' ? slot.match(/^(\d{1,2}):/) : null;
      const hour = match ? Number(match[1]) : null;
      if (!Number.isInteger(hour)) return 0;
      const rate = getHourlyRate(hour, dayType);
      if (!rate) return 0;
      total += rate;
    }
    return total;
  };

  const fallbackNormalAmount = calculateTotalAmount(
    booking?.time_slots,
    booking?.booking_date || format(new Date(), 'yyyy-MM-dd')
  );
  const normalAmount =
    Number(payment?.normal_amount) > 0
      ? Number(payment.normal_amount)
      : Number(booking?.normal_amount) > 0
        ? Number(booking.normal_amount)
        : Number(fallbackNormalAmount || 0);
  const discountAmount =
    Number(payment?.discount_amount) > 0
      ? Number(payment.discount_amount)
      : Number(booking?.discount_amount) > 0
        ? Number(booking.discount_amount)
        : 0;
  const discountSource = String(payment?.discount_source || booking?.discount_source || 'none');
  const totalPayable =
    Number(payment?.total_amount) > 0 ? Number(payment.total_amount) : Math.max(0, normalAmount - discountAmount);
  const discountSourceLabel =
    discountSource === 'member' ? 'Membership' : discountSource === 'voucher' ? 'Voucher' : '-';

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'Menunggu Verifikasi', class: 'bg-secondary/10 text-secondary border-secondary/20' },
      verified: { text: 'Terkonfirmasi', class: 'bg-primary/10 text-primary border-primary/20' },
      rejected: { text: 'Ditolak', class: 'bg-red-500/10 text-red-500 border-red-500/20' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase border ${badge.class}`}>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">Zero Futsal</span>
            </Link>
            <Link to="/my-bookings" className="text-zinc-400 hover:text-primary font-medium transition-colors">
              Lihat Semua Booking
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Success Message */}
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-4"
            >
              <CheckCircle className="w-12 h-12 text-primary" />
            </motion.div>
            <h1 className="text-4xl font-heading font-bold italic uppercase text-white mb-2">
              Booking Berhasil!
            </h1>
            <p className="text-zinc-400 text-lg">Terima kasih telah melakukan booking di Zero Futsal</p>
          </div>

          {/* Booking Details */}
          <div className="bg-zinc-900 border-l-4 border-primary rounded-r-lg p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">{venue?.name}</h2>
                <p className="text-zinc-400">Booking ID: {booking?.id?.slice(0, 8)}</p>
              </div>
              {getStatusBadge(payment?.status || 'pending')}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-primary mt-1" />
                <div>
                  <div className="text-sm text-zinc-400">Tanggal</div>
                  <div className="text-white font-bold">{booking?.booking_date}</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-primary mt-1" />
                <div>
                  <div className="text-sm text-zinc-400">Jam</div>
                  <div className="text-white font-bold">
                    {Array.isArray(booking?.time_slots) 
                      ? booking.time_slots.join(', ') 
                      : booking?.time_slot || '-'}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-primary mt-1" />
                <div>
                  <div className="text-sm text-zinc-400">Nama Pemesan</div>
                  <div className="text-white font-bold">{booking?.customer_name}</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-primary mt-1" />
                <div>
                  <div className="text-sm text-zinc-400">No. Handphone</div>
                  <div className="text-white font-bold">{booking?.customer_phone}</div>
                </div>
              </div>

              <div className="flex items-start gap-3 md:col-span-2">
                <MapPin className="w-5 h-5 text-primary mt-1" />
                <div>
                  <div className="text-sm text-zinc-400">Alamat</div>
                  <div className="text-white font-bold">{booking?.customer_address}</div>
                </div>
              </div>

              <div className="flex items-start gap-3 md:col-span-2 border-t border-zinc-700 pt-4">
                <CreditCard className="w-5 h-5 text-primary mt-1" />
                <div className="flex-1">
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="text-sm text-zinc-400">Harga normal</div>
                    <div className="text-white text-right font-semibold">
                      Rp {Number(normalAmount || 0).toLocaleString('id-ID')}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="text-sm text-zinc-400">Diskon</div>
                    <div className="text-white text-right font-semibold">
                      {discountAmount > 0 ? `- Rp ${Number(discountAmount || 0).toLocaleString('id-ID')}` : '-'}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="text-sm text-zinc-400">Sumber diskon</div>
                    <div className="text-white text-right">{discountSourceLabel}</div>
                  </div>
                  <div className="text-sm text-zinc-400">Total Bayar</div>
                  <div className="text-3xl font-black text-primary">
                    Rp {Number(totalPayable || 0).toLocaleString('id-ID')}
                  </div>
                  <div className="text-sm text-zinc-500 mt-1">
                    Metode: {payment?.method === 'qris' ? 'QRIS' : 'Transfer Bank'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Status Info */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
            <h3 className="text-lg font-bold text-white mb-4 uppercase font-heading">Status Pembayaran</h3>
            {payment?.status === 'pending' && (
              <div className="bg-secondary/10 border border-secondary/20 rounded-lg p-4">
                <p className="text-secondary font-semibold mb-2">⏳ Menunggu Verifikasi Admin</p>
                <p className="text-zinc-400 text-sm">
                  Bukti pembayaran Anda sedang diverifikasi oleh admin. Anda akan menerima notifikasi setelah pembayaran dikonfirmasi.
                </p>
              </div>
            )}
            {payment?.status === 'verified' && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <p className="text-primary font-semibold mb-2">✓ Pembayaran Terkonfirmasi</p>
                <p className="text-zinc-400 text-sm">
                  Booking Anda telah dikonfirmasi. Silakan datang ke lapangan sesuai jadwal yang telah ditentukan.
                </p>
              </div>
            )}
            {payment?.status === 'rejected' && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-red-500 font-semibold mb-2">✗ Pembayaran Ditolak</p>
                <p className="text-zinc-400 text-sm">
                  Maaf, pembayaran Anda ditolak. Silakan hubungi admin untuk informasi lebih lanjut.
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/"
              data-testid="back-to-home-btn"
              className="flex-1 bg-transparent border-2 border-primary text-primary hover:bg-primary/10 rounded-none skew-btn px-8 py-3 font-bold uppercase tracking-wider transition-all text-center"
            >
              <span>Kembali ke Beranda</span>
            </Link>
            <Link
              to="/my-bookings"
              data-testid="view-bookings-btn"
              className="flex-1 bg-primary text-black hover:bg-primary-hover rounded-none skew-btn px-8 py-3 font-bold uppercase tracking-wider transition-all text-center"
            >
              <span>Lihat Booking Saya</span>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ConfirmationPage;
